import { useState, FormEvent, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { recoverSessionFromStorage } from '../lib/authSessionPersistence'
import { validateSignupEmail } from '../lib/auth/validateSignupEmail'
import { getPostAuthLandingPath } from '../lib/resumeOnlyMode'
import { supabase } from '../lib/supabase'

import {
  logSignInAttempt,
  performSignIn,
  logSignInResponse,
  processSignInResponse,
  handleUnexpectedError,
  validatePasswordForSignup,
  checkPasswordsMatch,
  processSignUpResponse,
  handleOAuthError,
  performOAuthSignIn,
  hasOAuthError,
  handleOAuthUnexpectedError,
  handleModeSwitchToLogin,
  handleModeSwitchToSignup,
  getLoginTabClasses,
  getSignupTabClasses
} from './Login/handlers'
import { Button } from './Button'
import { GenufitAuthBrand } from './GenufitAuthBrand'
import { PasswordInput } from './Login/PasswordInput'
import { AuthMode } from './Login/types'

export type { AuthMode }

// CSS class constant to avoid duplication
const PASSWORD_VALID_CLASS = 'text-brand-primary'

function Login() {
  const [mode, setMode] = useState<AuthMode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const oauthError = searchParams.get('oauth_error')
    if (!oauthError) {
      return
    }
    setError(oauthError)
    setSearchParams((params) => {
      const next = new URLSearchParams(params)
      next.delete('oauth_error')
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    void (async () => {
      const user = await recoverSessionFromStorage(supabase)
      if (!user) return
      navigate(getPostAuthLandingPath({ onboardingComplete: true }), { replace: true })
    })()
  }, [navigate])

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      logSignInAttempt(email)
      const { data: signInData, error: signInError } = await performSignIn(email, password)
      logSignInResponse(signInData, signInError)
      await processSignInResponse(signInData, signInError, setLoading, setError, navigate)
    } catch (err: unknown) {
      handleUnexpectedError(err, setLoading, setError)
    }
  }



  const handleSignup = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!validatePasswordForSignup(password, setError)) {
      return
    }

    if (!checkPasswordsMatch(password, confirmPassword, setError)) {
      return
    }

    const emailCheck = await validateSignupEmail(email)
    if (!emailCheck.ok) {
      setError(emailCheck.message)
      return
    }

    setLoading(true)

    try {
      // Disable automatic email sending from Supabase
      // We will send email via SendGrid instead
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm`,
          // Disable automatic email sending - we use SendGrid
          // This requires SMTP to be disabled in Supabase Dashboard
        },
      })

      setLoading(false)

      await processSignUpResponse(data, signUpError, email, setError, navigate)
    } catch (err) {
      setLoading(false)
      setError('An unexpected error occurred. Please try again.')
    }
  }

  const handleSubmit = (e: FormEvent) => {
    if (mode === 'login') {
      handleLogin(e)
    } else {
      handleSignup(e)
    }
  }


  const handleOAuthSignIn = async (provider: 'google' | 'linkedin') => {
    setError(null)
    setLoading(true)

    try {
      const { error: oauthError } = await performOAuthSignIn(provider)

      if (hasOAuthError(oauthError)) {
        handleOAuthError(oauthError!, provider, setError, setLoading)
      }
      // Note: OAuth redirect will happen automatically via browser redirect
      // If redirect fails, loading will remain true, but user will be on OAuth provider page
    } catch (err: unknown) {
      handleOAuthUnexpectedError(err, provider, setLoading, setError)
    }
  }


  return (
    <div
      className="min-h-screen flex flex-col items-center justify-start bg-surface-subtle px-4 pt-16 pb-8 sm:pt-[64px]"
      data-testid="login-page"
    >
      <div className="flex w-full max-w-[400px] flex-col items-center gap-8">
        <GenufitAuthBrand />

        <div className="w-full rounded-lg bg-surface-default p-8 shadow-sm">
        <div
          className="flex border-b border-border-default"
          role="tablist"
          aria-label="Authentication mode"
          data-testid="auth-mode-tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            onClick={() => handleModeSwitchToLogin(setMode, setError, setPassword, setConfirmPassword, setPasswordFocused)}
            className={`flex-1 py-3 px-4 text-center text-body ${getLoginTabClasses(mode)}`}
            data-testid="auth-tab-sign-in"
          >
            Sign In
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'signup'}
            onClick={() => handleModeSwitchToSignup(setMode, setError, setPassword, setConfirmPassword, setPasswordFocused)}
            className={`flex-1 py-3 px-4 text-center text-body ${getSignupTabClasses(mode)}`}
            data-testid="auth-tab-sign-up"
          >
            Sign Up
          </button>
        </div>

        <div
          className="mt-6 min-h-[34px] flex items-center justify-center"
          aria-live="polite"
          data-testid="auth-mode-heading-slot"
        >
          <h2
            className="text-center text-h2 font-semibold text-text-primary"
            data-testid={mode === 'signup' ? 'auth-heading-sign-up' : 'auth-heading-sign-in'}
          >
            {mode === 'signup' ? 'Create your GenuFit account' : 'Sign in to your account'}
          </h2>
        </div>

        {/* OAuth Buttons */}
        <div className="mt-6">
          <div className="grid grid-cols-1 gap-3">
            <Button
              type="button"
              variant="outline"
              fullWidth
              disabled={loading}
              className="gap-2"
              onClick={() => handleOAuthSignIn('google')}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Continue with Google</span>
            </Button>
            {/* LinkedIn button - hidden for now, but code kept for future use */}
            {false && (
              <button
                type="button"
                onClick={() => handleOAuthSignIn('linkedin')}
                disabled={loading}
                className="w-full inline-flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0077B5">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                <span className="ml-2">LinkedIn</span>
              </button>
            )}
          </div>
        </div>

        {/* OAuth Divider */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border-strong" />
            </div>
            <div className="relative flex justify-center text-small">
              <span className="px-2 bg-surface-default text-text-muted">Or continue with email</span>
            </div>
          </div>
        </div>

        <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-danger-subtle text-danger-strong px-4 py-3 rounded-md">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-small font-medium text-text-secondary">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-border-strong placeholder:text-text-muted text-text-primary rounded-md bg-surface-default focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:border-interactive focus:z-10 text-small"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-small font-medium text-text-secondary">
                Password
              </label>
              <PasswordInput
                id="password"
                name="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                placeholder="Password"
                testId="password-input"
              />
              {mode === 'signup' && (passwordFocused || password.length > 0) && (
                <div className="mt-1 text-caption text-text-muted">
                  <p>Password requirements:</p>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li className={password.length >= 8 ? PASSWORD_VALID_CLASS : ''}>At least 8 characters</li>
                    <li className={/[A-Z]/.test(password) ? PASSWORD_VALID_CLASS : ''}>One uppercase letter</li>
                    <li className={/[a-z]/.test(password) ? PASSWORD_VALID_CLASS : ''}>One lowercase letter</li>
                    <li className={/\d/.test(password) ? PASSWORD_VALID_CLASS : ''}>One number</li>
                    <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? PASSWORD_VALID_CLASS : ''}>One special character</li>
                  </ul>
                </div>
              )}
            </div>
            {mode === 'signup' && (
              <div data-testid="confirm-password-field">
                <label htmlFor="confirmPassword" className="block text-small font-medium text-text-secondary">
                  Confirm Password
                </label>
                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  testId="confirm-password-input"
                />
              </div>
            )}
          </div>

          <div>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              loadingLabel={mode === 'login' ? 'Signing in…' : 'Creating account…'}
            >
              {mode === 'login' ? 'Sign in' : 'Create account'}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </div>
  )
}

export default Login

